/**
 * useKhorosKudos hook helper
 * Interface with Khoros Kudos functionality REST API
 *
 * Author: Relevant Bits
 */

import { useContext } from 'react';

import { useQuery } from '@tanstack/react-query';
import type { CacheRequestConfig } from 'axios-cache-interceptor';

import { ConfigContext } from '../../../../aurr/contexts/ConfigContext';
import LoginContext from '../../../../aurr/contexts/LoginContext';
import { cacheTTLs } from '../../../../common/constants';
import { buildCacheKey, getAxiosInstance } from '../../../axiosCache';
import { KUDOS_QUERY_KEY } from '../../constants';
import { KhorosTopicProps } from '../../interface/khoros.interface';
import { KhorosKudosGetSchema } from '../../type/khoros-kudos.type';
import { useKhorosTopic } from '../useKhorosTopic';
import { useKhorosUser } from '../useKhorosUser';

export const useKhorosKudos = ({ topicId, baseURL }: KhorosTopicProps) => {
  const { KHOROS_API } = useContext(ConfigContext);
  // create axios client used in all community function requests
  const { access_token, isUserLoading, userProfile } = useContext(LoginContext);
  const sub = userProfile?.sub;

  const axiosConfig: CacheRequestConfig = {
    baseURL: baseURL || KHOROS_API,
    headers: {
      ...(access_token && { authorization: `Bearer ${access_token}` }),
      'Cache-Control': 'no-cache, max-age=0',
    },
    id: buildCacheKey([KUDOS_QUERY_KEY, topicId, sub]),
    cache: {
      ttl: cacheTTLs.kudos,
    },
  };

  const { isLoading: khorosUserLoading, data: userResponse } = useKhorosUser({
    baseURL,
  });

  const { isLoading: topicDataLoading, data: topicData } = useKhorosTopic({
    topicId,
    baseURL,
  });

  let isAuthor = false;
  if (topicData && userResponse?.kh_user_id) {
    isAuthor = topicData?.kh_author_user_id === userResponse.kh_user_id;
  }

  const api = getAxiosInstance();

  const {
    isLoading: getIsLoading,
    error,
    data,
  } = useQuery<KhorosKudosGetSchema | undefined>({
    queryKey: [KUDOS_QUERY_KEY, { topicId, sub }],
    queryFn: async () => {
      const response = await api.get(
        `/services/v2/community/khoros/kudos?id=${topicId}`,
        axiosConfig
      );
      return response?.data || undefined;
    },
    enabled: !isUserLoading,
  });

  const waitingForUser = access_token && khorosUserLoading && topicDataLoading;

  return {
    isLoading: waitingForUser || getIsLoading,
    isAuthor,
    error,
    data: data as KhorosKudosGetSchema,
  };
};
